import { v4 as uuidv4 } from 'uuid';
import { createComponentStore } from '../../utils/createComponentStore';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { send_component_save_request } from '../../utils/Request';
import useProjectStore from '../ProjectCanvas/useProjectStore';

const MODE = 'CURL';

export const emptyCurlTemplate = () => ({
  appName: '',
  action: {
    appName: '',
    action: '',
    description: '',
    requirements: [],
    endpoint: '',
    curl: '',
    fields: [],
  },
  credentialPlaceholder: '',
});

const keyAuto = () => 'api' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const initRequestData = () => ({
  request_type: 'POST',
  data_type: 'JSON',
  endpoint_url: '',
  body: '',
  headers: [],
  curl: '',
  setupMode: MODE,
  skipSample: true,
  rawResponsePlaceholderName: 'apiResponse' + Math.floor(1000 + Math.random() * 9000),
});

const initSampleData = () => ({
  request_type: 'POST',
  data_type: 'JSON',
  endpoint_url: '',
  body: '',
  headers: [],
  curl: '',
  setupMode: MODE,
});

const dataField = () => ({
  key: keyAuto(),
  sampleKey: '',
  sampleData: '',
  checked: true,
});

const componentData = () => ({
  type: 'MANUALLY',
  json: '',
  samplePlaceholders: [dataField()],
  placeholders: [],
  timeout: 120,
});

// ---- Factory data shape ----
// The canonical data lives under store.data.{requestData, sampleData, responseData, curlTemplateData}
// For backward compatibility, top-level aliases (store.requestData, etc.) are kept in sync.

const initData = () => ({
  requestData: initRequestData(),
  sampleData: initSampleData(),
  responseData: componentData(),
  curlTemplateData: emptyCurlTemplate(),
});

/**
 * Helper: given a new data object, returns the top-level alias fields that must
 * be set alongside `data` so that `store.getState().requestData` etc. keep working.
 */
const topLevelAliases = (d) => ({
  requestData: d.requestData,
  sampleData: d.sampleData,
  responseData: d.responseData,
  curlTemplateData: d.curlTemplateData,
});

/**
 * onLoadData — transforms the raw backend componentData into our nested data shape.
 * The backend may send requestData/sampleData/responseData at the top level of
 * componentData, or nested under componentData.data.
 */
const onLoadData = (rawData, component) => {
  const cd = component.componentData || {};
  const defaults = initData();

  // requestData
  let requestData;
  if (cd.requestData) {
    requestData = { ...cd.requestData };
    if (!requestData.setupMode) requestData.setupMode = MODE;
  } else if (rawData.requestData) {
    requestData = { ...rawData.requestData };
    if (!requestData.setupMode) requestData.setupMode = MODE;
  } else {
    requestData = defaults.requestData;
  }

  // sampleData
  let sampleData;
  if (cd.sampleData) {
    sampleData = { ...cd.sampleData };
    if (!sampleData.setupMode) sampleData.setupMode = MODE;
  } else if (rawData.sampleData) {
    sampleData = { ...rawData.sampleData };
    if (!sampleData.setupMode) sampleData.setupMode = MODE;
  } else {
    sampleData = defaults.sampleData;
  }

  // responseData
  let responseData;
  if (cd.responseData) {
    responseData = cd.responseData;
  } else if (rawData.responseData) {
    responseData = rawData.responseData;
  } else {
    responseData = defaults.responseData;
  }

  // curlTemplateData (lives under componentData.data.curlTemplateData)
  const ctd = cd.data?.curlTemplateData || rawData.curlTemplateData;
  const curlTemplateData = (!ctd || Object.keys(ctd).length === 0)
    ? emptyCurlTemplate()
    : { ...ctd };

  // Legacy migration: old wizard components may have skipSample=false and store
  // output placeholder keys in responseData.samplePlaceholders/placeholders instead
  // of requestData.rawResponsePlaceholderName. Migrate so inline config can display them.
  if (!requestData.rawResponsePlaceholderName) {
    const legacyKey =
      responseData?.samplePlaceholders?.[0]?.key ||
      responseData?.placeholders?.[0]?.key;
    if (legacyKey) {
      requestData.rawResponsePlaceholderName = legacyKey;
      requestData.skipSample = true;
    } else {
      // Generate a default so the field isn't blank
      requestData.rawResponsePlaceholderName = 'apiResponse' + Math.floor(1000 + Math.random() * 9000);
      requestData.skipSample = true;
    }
  }

  return { requestData, sampleData, responseData, curlTemplateData };
};

const { getStore, defaultStore } = createComponentStore({
  type: 'request',
  endpoint: 'apirequestandresponse',
  registryKey: 'apirequestandresponse',
  initData,
  onLoadData,

  extraState: (set, get) => ({
    // Backward-compat top-level aliases — kept in sync by every setter
    requestData: initRequestData(),
    sampleData: initSampleData(),
    responseData: componentData(),
    curlTemplateData: emptyCurlTemplate(),

    // Additional top-level state that existed in the old store
    endpointPlaceholders: [],
    placeholderError: [],
    placeholders: [],
    imageContext: null,
    position: null,
    error: false,
    json: '',
  }),

  extraActions: (set, get) => ({
    // ---- Override setComponentData so top-level aliases stay in sync ----
    setComponentData: (component) => {
      // Delegate to populateFromComponent which handles everything
      get().populateFromComponent(component);
    },

    // ---- Override initialiseEmpty to also reset top-level aliases ----
    initialiseEmpty: () => {
      const d = initData();
      set({
        loaded: false,
        componentId: '',
        name: '',
        lastModified: '',
        data: d,
        ...topLevelAliases(d),
        endpointPlaceholders: [],
        placeholderError: [],
        placeholders: [],
        imageContext: null,
        position: null,
        error: false,
        json: '',
      });
    },

    // Legacy alias — kept for prefillComponentStores which calls store.populateFromComponent
    populateFromComponent: (component) => {
      const cd = component.componentData;
      const d = onLoadData(cd && cd.data != null ? (typeof cd.data === 'object' ? cd.data : {}) : (cd || {}), component);

      const updates = {
        componentId: component.componentId,
        type: component.type,
        data: d,
        ...topLevelAliases(d),
      };

      if (cd) {
        updates.name = cd.name || '';
        updates.lastModified = cd.lastModified || '';
      } else {
        updates.name = '';
        updates.lastModified = '';
      }

      set(updates);
    },

    resetStore: () => {
      const d = initData();
      set({
        loaded: false,
        componentId: '',
        name: '',
        lastModified: '',
        data: d,
        ...topLevelAliases(d),
        endpointPlaceholders: [],
        placeholderError: [],
        placeholders: [],
        imageContext: null,
        position: null,
        error: false,
        json: '',
      });
    },

    // ---- Override getTemplate to return the correct shape for saving ----
    getTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        type: s.type,
        lastModified: s.lastModified,
        data: {
          requestData: JSON.parse(JSON.stringify(s.data.requestData)),
          sampleData: JSON.parse(JSON.stringify(s.data.sampleData)),
          responseData: JSON.parse(JSON.stringify(s.data.responseData)),
          curlTemplateData: JSON.parse(JSON.stringify(s.data.curlTemplateData)),
        },
      };
    },

    // ---- Custom save for inline editing ----
    save: () => {
      const s = get();

      // Build response placeholders
      const responseData = JSON.parse(JSON.stringify(s.data.responseData));
      responseData.placeholders = [];
      if (responseData.samplePlaceholders) {
        for (const sp of responseData.samplePlaceholders) {
          if (sp.checked) responseData.placeholders.push(sp);
        }
      }

      const ps = useProjectStore.getState();
      const payload = {
        componentData: {
          schemaVersion: 2,
          name: s.name || ps.componentName || '',
          requestData: JSON.parse(JSON.stringify(s.data.requestData)),
          sampleData: JSON.parse(JSON.stringify(s.data.sampleData)),
          responseData,
          lastModified: getCurrentTime(),
          data: {
            curlTemplateData: JSON.parse(JSON.stringify(s.data.curlTemplateData)),
          },
        },
        componentId: s.componentId,
        description: s.componentDescription || ps.componentDescription || '',
        metadataEnabled: ps.metadataEnabled,
        startTimestampLabel: ps.startTimestampLabel,
        endTimestampLabel: ps.endTimestampLabel,
        metadataTimestampFormat: ps.metadataTimestampFormat,
      };

      return send_component_save_request(
        'component-service/apirequestandresponse/data',
        payload,
        '',
        'POST',
      );
    },

    // ---- Template setters (legacy, used by APIResReqTemplate.js) ----

    setRequestTemplate: (component) => {
      get().resetStore();
      const updates = { componentId: component.componentId, type: component.type };
      if (component.componentData) {
        const rd = { ...component.componentData.requestData };
        if (!rd.setupMode) rd.setupMode = MODE;
        updates.data = { ...get().data, requestData: rd };
        updates.requestData = rd;
        updates.lastModified = component.componentData.lastModified;
        updates.name = component.componentData.name;
      } else {
        const rd = initRequestData();
        updates.data = { ...get().data, requestData: rd };
        updates.requestData = rd;
        updates.lastModified = '';
        updates.name = '';
      }
      set(updates);
    },

    updateRequestData: (data) => {
      if (data && data.curl) {
        const rd = { ...get().data.requestData, curl: data.curl, setupMode: data.setupMode, skipSample: data.skipSample };
        set((s) => ({
          data: { ...s.data, requestData: rd },
          requestData: rd,
        }));
      }
    },

    setCurlTemplateData: (component) => {
      const ctd = component?.componentData?.data?.curlTemplateData;
      const isEmpty = !ctd || Object.keys(ctd).length === 0;
      const val = isEmpty ? emptyCurlTemplate() : { ...ctd };
      set((s) => ({
        data: { ...s.data, curlTemplateData: val },
        curlTemplateData: val,
      }));
    },

    setSampleTemplate: (component) => {
      const updates = { componentId: component.componentId, type: component.type };
      if (component.componentData) {
        const sd = { ...component.componentData.sampleData };
        if (!sd.setupMode) sd.setupMode = MODE;
        updates.data = { ...get().data, sampleData: sd };
        updates.sampleData = sd;
        updates.lastModified = component.componentData.lastModified;
        updates.name = component.componentData.name;
      } else {
        const sd = initSampleData();
        updates.data = { ...get().data, sampleData: sd };
        updates.sampleData = sd;
      }
      set(updates);
    },

    updateSampleData: (data) => {
      if (data) {
        const sd = { ...get().data.sampleData, curl: data.curl, setupMode: data.setupMode };
        set((s) => ({
          data: { ...s.data, sampleData: sd },
          sampleData: sd,
        }));
      }
    },

    setResponseTemplate: (component) => {
      const updates = { componentId: component.componentId, type: component.type };
      if (component.componentData) {
        const rd = component.componentData.responseData;
        updates.data = { ...get().data, responseData: rd };
        updates.responseData = rd;
        updates.lastModified = component.componentData.lastModified;
        updates.name = component.componentData.name;
      } else {
        const rd = componentData();
        updates.data = { ...get().data, responseData: rd };
        updates.responseData = rd;
        updates.lastModified = '';
        updates.name = '';
      }
      set(updates);
    },

    // ---- Granular request setters ----

    setRequestType: (requestType) => {
      const rd = { ...get().data.requestData, request_type: requestType };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    setDataType: (dataType) => {
      const rd = { ...get().data.requestData, data_type: dataType };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    setEndPointUrl: (endPointUrl) => {
      const rd = { ...get().data.requestData, endpoint_url: endPointUrl };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    setApiReqBody: (body) => {
      const rd = { ...get().data.requestData, body };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    setSkipSample: (skipSample) => {
      const rd = { ...get().data.requestData, skipSample };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    isSkipSample: () => get().data.requestData.skipSample,

    setSetupRequestMode: (mode) => {
      const rd = { ...get().data.requestData, setupMode: mode };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    setRequestCurl: (curl) => {
      const rd = { ...get().data.requestData, curl };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    setRawResponsePlaceholder: (placeholderName) => {
      const rd = { ...get().data.requestData, rawResponsePlaceholderName: placeholderName };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    setRequestDataHeaders: (headers) => {
      const rd = { ...get().data.requestData, headers: [...headers] };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    addCustomHeaders: () => {
      const newHeader = { header_id: uuidv4(), header_name: '', header_value: '' };
      const rd = { ...get().data.requestData, headers: [...get().data.requestData.headers, newHeader] };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    deleteCustomHeader: (placeholder) => {
      const headers = [...get().data.requestData.headers];
      const index = headers.findIndex((x) => x.key === placeholder.key);
      if (index !== -1) headers.splice(index, 1);
      const rd = { ...get().data.requestData, headers };
      set((s) => ({ data: { ...s.data, requestData: rd }, requestData: rd }));
    },

    // ---- Granular sample setters ----

    setSampleRequestType: (requestType) => {
      const sd = { ...get().data.sampleData, request_type: requestType };
      set((s) => ({ data: { ...s.data, sampleData: sd }, sampleData: sd }));
    },

    setSampleDataType: (dataType) => {
      const sd = { ...get().data.sampleData, data_type: dataType };
      set((s) => ({ data: { ...s.data, sampleData: sd }, sampleData: sd }));
    },

    setSampleEndPointUrl: (endPointUrl) => {
      const sd = { ...get().data.sampleData, endpoint_url: endPointUrl };
      set((s) => ({ data: { ...s.data, sampleData: sd }, sampleData: sd }));
    },

    setSampleApiReqBody: (body) => {
      const sd = { ...get().data.sampleData, body };
      set((s) => ({ data: { ...s.data, sampleData: sd }, sampleData: sd }));
    },

    setSetupSampleMode: (mode) => {
      const sd = { ...get().data.sampleData, setupMode: mode };
      set((s) => ({ data: { ...s.data, sampleData: sd }, sampleData: sd }));
    },

    setSampleCurl: (curl) => {
      const sd = { ...get().data.sampleData, curl };
      set((s) => ({ data: { ...s.data, sampleData: sd }, sampleData: sd }));
    },

    addSampleCustomHeaders: () => {
      const newHeader = { header_id: uuidv4(), header_name: '', header_value: '' };
      const sd = { ...get().data.sampleData, headers: [...get().data.sampleData.headers, newHeader] };
      set((s) => ({ data: { ...s.data, sampleData: sd }, sampleData: sd }));
    },

    // ---- Granular response setters ----

    setField: (value, index, type) => {
      const rd = JSON.parse(JSON.stringify(get().data.responseData));
      switch (type) {
        case 'sample':
          rd.json = value;
          break;
        case 'key':
          rd[index].key = value;
          break;
        case 'type':
          rd.type = value;
          rd.samplePlaceholders = [];
          break;
        case 'content':
          rd[index].content = value;
          break;
        default:
          break;
      }
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    addRow: () => {
      const obj = dataField();
      const rd = { ...get().data.responseData, samplePlaceholders: [...get().data.responseData.samplePlaceholders, obj] };
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    jsonData: (key, value) => ({
      key,
      sampleKey: key,
      sampleData: value,
      checked: true,
    }),

    addJsonData: (obj) => {
      const rd = { ...get().data.responseData, samplePlaceholders: [...get().data.responseData.samplePlaceholders, obj] };
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    deleteRow: (placeholder) => {
      const sps = [...get().data.responseData.samplePlaceholders];
      const index = sps.findIndex((x) => x.key === placeholder.key);
      if (index !== -1) sps.splice(index, 1);
      const rd = { ...get().data.responseData, samplePlaceholders: sps };
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    removeKeyInPlaceholder: (placeholderKey) => {
      const phs = [...get().data.responseData.placeholders];
      const index = phs.findIndex((x) => x.key === placeholderKey);
      if (index !== -1) phs.splice(index, 1);
      const rd = { ...get().data.responseData, placeholders: phs };
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    addKeyInPlaceholder: (placeholderKey, placeholderType) => {
      const obj = { key: placeholderKey, type: placeholderType, length: 1 };
      const phs = [...get().data.responseData.placeholders, obj];
      const rd = { ...get().data.responseData, placeholders: phs };
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    onChangeField: (value, placeholder, type) => {
      const rd = JSON.parse(JSON.stringify(get().data.responseData));
      switch (type) {
        case 'content': {
          const index = rd.samplePlaceholders.findIndex((x) => x.key === placeholder.key);
          if (index !== -1) {
            rd.samplePlaceholders[index].sampleKey = value;
            rd.samplePlaceholders[index].sampleData = '';
          }
          break;
        }
        case 'json':
          rd.json = value;
          break;
        case 'key': {
          const index = rd.samplePlaceholders.findIndex((x) => x.key === placeholder.key);
          if (index !== -1) rd.samplePlaceholders[index].key = value;
          break;
        }
        case 'timeout':
          rd.timeout = value;
          break;
        default:
          break;
      }
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    clearField: () => {
      const rd = { ...get().data.responseData, samplePlaceholders: [] };
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    setResponseDataPlaceholders: (placeholders) => {
      const rd = { ...get().data.responseData, placeholders: [...placeholders] };
      set((s) => ({ data: { ...s.data, responseData: rd }, responseData: rd }));
    },

    // ---- Granular curlTemplate setters ----

    setCurlTemplateDataAppName: (appName) => {
      const ctd = { ...get().data.curlTemplateData, appName };
      set((s) => ({ data: { ...s.data, curlTemplateData: ctd }, curlTemplateData: ctd }));
    },

    setCurlTemplateDataAction: (action) => {
      const ctd = { ...get().data.curlTemplateData, action };
      set((s) => ({ data: { ...s.data, curlTemplateData: ctd }, curlTemplateData: ctd }));
    },

    setCurlTemplateDataCredentialPlaceholder: (credentialPlaceholder) => {
      const ctd = { ...get().data.curlTemplateData, credentialPlaceholder };
      set((s) => ({ data: { ...s.data, curlTemplateData: ctd }, curlTemplateData: ctd }));
    },

    setCurlTemplateDataFields: (actionFields) => {
      const ctd = {
        ...get().data.curlTemplateData,
        action: { ...get().data.curlTemplateData.action, fields: actionFields },
      };
      set((s) => ({ data: { ...s.data, curlTemplateData: ctd }, curlTemplateData: ctd }));
    },

    // ---- Misc setters ----

    setRequestName: (name) => set({ name }),
    setAPIName: (name) => set({ name }),
    setEndpointPlaceholders: (placeholders) => set({ endpointPlaceholders: [...placeholders] }),
    storeError: (obj) => set({ placeholderError: obj }),

    keyAuto,

    // ---- Sub-template getters (legacy, used by APIResReqTemplate.js) ----

    getRequestTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        requestData: JSON.parse(JSON.stringify(s.data.requestData)),
        type: s.type,
        lastModified: s.lastModified,
        endpointPlaceholders: [...(s.endpointPlaceholders || [])],
      };
    },

    getSampleTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        sampleData: JSON.parse(JSON.stringify(s.data.sampleData)),
        type: s.type,
        lastModified: s.lastModified,
      };
    },

    getResponseTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        position: s.position,
        responseData: JSON.parse(JSON.stringify(s.data.responseData)),
        type: s.type,
        lastModified: s.lastModified,
        json: s.json,
      };
    },

    getCurlTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        type: s.type,
        lastModified: s.lastModified,
        curlTemplateData: JSON.parse(JSON.stringify(s.data.curlTemplateData)),
      };
    },
  }),
});

export const getAPIResReqStore = getStore;
export default defaultStore;
